import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, NgZone, OnDestroy, Output } from '@angular/core';

/**
 * Emits `scrolled` when the host element comes into view (works for window and nested scroll containers).
 * Place it on a sentinel element below a list and re-create the sentinel after every load
 * (e.g. `@if (hasMore() && !isLoadingMore())`), so a still-visible sentinel triggers the next page.
 */
@Directive({
  selector: '[appInfiniteScroll]',
  standalone: true,
})
export class InfiniteScrollDirective implements AfterViewInit, OnDestroy {
  /** Distance to the viewport edge at which the next page is requested. */
  @Input() infiniteScrollMargin: string = '200px';

  @Output() scrolled: EventEmitter<void> = new EventEmitter<void>();

  private observer: IntersectionObserver | null = null;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly ngZone: NgZone,
  ) {}

  public ngAfterViewInit(): void {
    this.observer = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]): void => {
        if (entries.some((entry: IntersectionObserverEntry): boolean => entry.isIntersecting)) {
          this.ngZone.run((): void => this.scrolled.emit());
        }
      },
      { rootMargin: this.infiniteScrollMargin },
    );

    this.observer.observe(this.elementRef.nativeElement);
  }

  public ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
